import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { useState } from "react";

import { IDraftMessage } from "../../../store/queries/contractModelDrafts";
import { StepId } from "./steps";

interface IProps {
  stepId: StepId;
  messages: IDraftMessage[];
  busy: boolean;
  regenerating: boolean;
  onAdd: (body: string, stepId: StepId) => Promise<void>;
  onDelete: (messageId: string) => void;
  onRegenerate: () => void;
}

/**
 * "Requested changes" — the one queue of instructions the agent applies on the next Regenerate.
 *
 * There is a SINGLE mechanism here, and this panel is the whole of it. The per-recommendation
 * "Request a change" button opens a modal that feeds the very same queue (scoped to a recommendation);
 * everything typed here is whole-model. So the panel shows ALL pending requests, wherever they were
 * written, and it is the same panel on every step — because a regeneration is global, never per-step.
 *
 * Two things that look like omissions but are deliberate:
 *  - **Not a chat.** Generation is a multi-minute batch; the agent can't reply live. A numbered queue,
 *    not speech bubbles.
 *  - **Adding a request does not start a generation.** They accumulate; you decide when to Regenerate.
 *
 * The old version spelled all of this out in three paragraphs on every step — a wall of text. It now
 * lives behind "How this works", so the panel is compact by default.
 */
function InstructionPanel({
  stepId,
  messages,
  busy,
  regenerating,
  onAdd,
  onDelete,
  onRegenerate,
}: IProps) {
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const pending = messages.filter((m) => !m.appliedInVersionId);

  const submit = async () => {
    const text = body.trim();
    if (!text) return;
    setSaving(true);
    try {
      await onAdd(text, stepId);
      setBody("");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="surface-card border-round p-3 mt-4 border-1 surface-border">
      <div className="flex align-items-center justify-content-between mb-1">
        <div className="font-semibold">
          Requested changes
          {pending.length > 0 ? (
            <span className="text-color-secondary font-normal">
              {" "}
              ({pending.length} pending)
            </span>
          ) : null}
        </div>
        <Button
          label={showHelp ? "Hide" : "How this works"}
          className="p-button-text p-button-sm p-0"
          onClick={() => setShowHelp((v) => !v)}
        />
      </div>

      <p className="mt-0 mb-2 text-sm text-600 line-height-3">
        Tell the agent what to change; it&rsquo;s applied on the next
        Regenerate, not now.
      </p>

      {showHelp ? (
        <p className="mt-0 mb-3 text-sm text-700 line-height-3 surface-50 border-round p-2">
          Requests <strong>queue up</strong> across every step and nothing
          happens until you press <strong>Regenerate</strong>, which re-reads
          the documents and rebuilds the whole model, applying{" "}
          <strong>all</strong> of them at once. Recommendations that don&rsquo;t
          change keep the verdict you already gave, so you never review the same
          thing twice. If you already know the right value, it&rsquo;s quicker
          to edit the fields and press Confirm &mdash; that takes effect
          immediately, with no rebuild.
        </p>
      ) : null}

      {messages.length > 0 && (
        <ol className="mt-2 mb-3 pl-3 text-sm">
          {messages.map((m) => (
            <li key={m.id} className="mb-2">
              <span
                className={
                  m.appliedInVersionId ? "text-color-secondary" : "text-900"
                }
              >
                {m.body}
              </span>
              <div className="flex align-items-center gap-2 mt-1">
                {/* Scope — the same queue holds both whole-model requests and ones tied to a single
                    recommendation. Saying which keeps them from reading as duplicates. */}
                <small className="text-color-secondary">
                  {m.recommendationId
                    ? "for one recommendation"
                    : "for the whole model"}
                </small>
                {m.appliedInVersionId ? (
                  <small className="text-color-secondary">
                    <i
                      className="pi pi-check mr-1"
                      style={{ fontSize: "0.7rem" }}
                    />
                    applied
                  </small>
                ) : (
                  <>
                    <small className="text-orange-700 font-medium">
                      Not applied yet
                    </small>
                    <Button
                      label="Remove"
                      className="p-button-text p-button-sm p-0"
                      disabled={busy}
                      onClick={() => onDelete(m.id)}
                    />
                  </>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}

      <InputTextarea
        className="w-full"
        rows={2}
        autoResize
        placeholder="e.g. Treat the office column as a separate entity. The psych codes follow the 2024 grid, not the amendment."
        value={body}
        disabled={busy || saving || regenerating}
        onChange={(e) => setBody(e.target.value)}
      />

      <div className="flex justify-content-end align-items-center mt-2 gap-2">
        <Button
          label="Request change"
          icon="pi pi-plus"
          className="p-button-sm p-button-outlined"
          disabled={busy || saving || regenerating || body.trim().length === 0}
          onClick={submit}
        />
        {/* The rebuild is reachable from where the reviewer is thinking, not only from the bar up top. */}
        <Button
          label={
            regenerating
              ? "Regenerating…"
              : `Regenerate${pending.length > 0 ? ` (${pending.length})` : ""}`
          }
          icon="pi pi-refresh"
          className="p-button-sm"
          disabled={busy || regenerating || pending.length === 0}
          onClick={onRegenerate}
        />
      </div>
    </div>
  );
}

export default InstructionPanel;
